import { supabase } from "@/integrations/supabase/client";
import { type Visagist, type VisagistTheme } from "@/data/visagists";

export type { Visagist, VisagistTheme };

const KNOWN: VisagistTheme[] = ["day-makeup", "evening-makeup", "bridal", "editorial", "open"];

export function normalizeVisagistTheme(t: string): VisagistTheme {
  const lower = (t ?? "").toLowerCase().trim();
  if (!lower) return "open";
  if (lower.includes("brid") || lower.includes("braut") || lower.includes("wedding") || lower.includes("hochzeit")) return "bridal";
  if (lower.includes("editor") || lower.includes("fashion") || lower.includes("mode") || lower.includes("kampagne") || lower.includes("kampagnen")) return "editorial";
  if (lower.includes("abend") || lower.includes("evening") || lower.includes("gala") || lower.includes("glam")) return "evening-makeup";
  if (lower.includes("tag") || lower.includes("day") || lower.includes("natural") || lower.includes("nude")) return "day-makeup";
  if (KNOWN.includes(lower as VisagistTheme)) return lower as VisagistTheme;
  return "open";
}

type DbRow = {
  id: string;
  slug: string;
  name: string;
  region: string | null;
  themes: string[] | null;
  bio: string | null;
  email: string | null;
  portrait_url: string | null;
  gallery_urls: string[] | null;
  country?: string | null;
  city?: string | null;
  price_from?: number | null;
  tfp?: boolean | null;
  price_on_request?: boolean | null;
  website_url?: string | null;
  instagram_handle?: string | null;
  portrait_position?: string | null;
  portrait_zoom?: number | null;
  gallery_themes?: string[] | null;
  shooting_settings?: string[] | null;
  languages?: string[] | null;
  founding_member?: boolean | null;
};

function rowToVisagist(row: DbRow): Visagist {
  const portrait = row.portrait_url ?? "/placeholder.svg";
  const gallery = (row.gallery_urls ?? []).filter(Boolean);
  const rawThemes = row.gallery_themes ?? [];
  const galleryThemes = gallery.map((_, i) => normalizeVisagistTheme(rawThemes[i] ?? "open"));
  return {
    id: row.slug,
    name: row.name,
    region: row.region ?? "",
    themes: (row.themes ?? []).map(normalizeVisagistTheme),
    priceFrom: row.price_from ?? 0,
    tfp: row.tfp ?? false,
    priceOnRequest: row.price_on_request ?? false,
    bio: row.bio ?? "",
    email: row.email ?? "",
    portrait,
    gallery: gallery.length ? gallery : [portrait],
    galleryThemes,
    country: row.country ?? null,
    city: row.city ?? null,
    websiteUrl: row.website_url ?? null,
    instagramHandle: row.instagram_handle ?? null,
    portraitPosition: row.portrait_position ?? "50% 50%",
    portraitZoom: row.portrait_zoom ?? 1,
    shootingSettings: row.shooting_settings ?? [],
    languages: row.languages ?? [],
    foundingMember: row.founding_member ?? false,
  };
}

export async function fetchAllVisagists(): Promise<Visagist[]> {
  const { data, error } = await supabase
    .from("visagists")
    .select("id, slug, name, region, themes, bio, email, portrait_url, gallery_urls, gallery_themes, country, city, price_from, tfp, price_on_request, website_url, instagram_handle, portrait_position, portrait_zoom, shooting_settings, languages, founding_member")
    .eq("published", true)
    .order("created_at", { ascending: false });
  if (error) {
    console.error("[visagists]", error);
    return [];
  }
  return (data ?? []).map((r) => rowToVisagist(r as DbRow));
}

export async function fetchVisagistBySlug(slug: string): Promise<Visagist | null> {
  const { data, error } = await supabase
    .from("visagists")
    .select("id, slug, name, region, themes, bio, email, portrait_url, gallery_urls, gallery_themes, country, city, price_from, tfp, price_on_request, website_url, instagram_handle, portrait_position, portrait_zoom, shooting_settings, languages, founding_member")
    .eq("slug", slug)
    .eq("published", true)
    .maybeSingle();
  if (!error && data) return rowToVisagist(data as DbRow);
  return null;
}